import { ICON_KEYS } from '@iac/contracts';
import type { LinkIconKey, UsefulLinkDto } from '@iac/contracts';
import { useState, type FormEvent } from 'react';
import { AppCard, AppCardSlot, AppGrid } from '../../components/AppCard';
import { DeleteButton } from '../../components/DeleteButton';
import { EmptyState } from '../../components/EmptyState';
import { Field, SelectField } from '../../components/Field';
import { AppIconFor, IconPencilSquare } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { useCreateLink, useDeleteLink, useLinks, useUpdateLink } from './api';
import { ag5Dict } from './i18n';

function iconOptions() {
  return ICON_KEYS.map((key) => (
    <option key={key} value={key}>
      {key}
    </option>
  ));
}

function LinkCardEdit({
  link,
  onDone,
}: {
  link: UsefulLinkDto;
  onDone: () => void;
}) {
  const t = useDict(ag5Dict);
  const [label, setLabel] = useState(link.label);
  const [url, setUrl] = useState(link.url);
  const [iconKey, setIconKey] = useState<LinkIconKey>(link.iconKey);
  const update = useUpdateLink();

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    update.mutate({ id: link.id, label, url, iconKey }, { onSuccess: onDone });
  }

  return (
    <form
      id={`link-card-${link.id}`}
      className="link-edit-form"
      onSubmit={onSubmit}
    >
      <Field
        id={`edit-label-${link.id}`}
        label={t.label}
        name="label"
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        autoComplete="off"
        required
        maxLength={100}
      />
      <Field
        id={`edit-url-${link.id}`}
        label={t.url}
        name="url"
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        autoComplete="off"
        required
      />
      <SelectField
        id={`edit-icon-${link.id}`}
        label={t.icon}
        name="iconKey"
        value={iconKey}
        onChange={(e) => setIconKey(e.target.value as LinkIconKey)}
        required
      >
        {iconOptions()}
      </SelectField>
      <div className="form-actions">
        <button type="button" className="btn-ghost btn-sm" onClick={onDone}>
          {t.cancel}
        </button>
        <button
          type="submit"
          className="btn-primary btn-sm"
          disabled={update.isPending}
        >
          {t.save}
        </button>
      </div>
    </form>
  );
}

function LinkCard({ link }: { link: UsefulLinkDto }) {
  const t = useDict(ag5Dict);
  const [editing, setEditing] = useState(false);
  const remove = useDeleteLink();

  if (editing) {
    return <LinkCardEdit link={link} onDone={() => setEditing(false)} />;
  }

  return (
    <AppCardSlot
      id={`link-card-${link.id}`}
      actions={
        <>
          <button
            type="button"
            className="btn-icon-ghost btn-icon-overlay"
            aria-label={t.editLink(link.label)}
            onClick={() => setEditing(true)}
          >
            <IconPencilSquare />
          </button>
          <DeleteButton
            className="btn-icon-danger btn-icon-overlay"
            label={t.deleteLink(link.label)}
            confirm={t.confirmDeleteLink(link.label)}
            onConfirm={() => remove.mutate(link.id)}
          />
        </>
      }
    >
      <AppCard
        to={link.url}
        external
        icon={<AppIconFor appKey={link.iconKey} />}
        title={link.label}
      />
    </AppCardSlot>
  );
}

export function UsefulLinks() {
  const t = useDict(ag5Dict);
  usePageTitle(t.linksTitle);
  const { data: links } = useLinks();
  const create = useCreateLink();
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [iconKey, setIconKey] = useState<LinkIconKey>(ICON_KEYS[0]);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(
      { label, url, iconKey },
      {
        onSuccess: () => {
          setLabel('');
          setUrl('');
          setIconKey(ICON_KEYS[0]);
        },
      },
    );
  }

  return (
    <>
      <PageHeader title={t.usefulLinks} />

      <Panel title={t.addLink}>
        <form onSubmit={onSubmit} className="form-grid-3plus">
          <Field
            id="new-link-label"
            label={t.label}
            name="label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoComplete="off"
            required
            maxLength={100}
          />
          <Field
            id="new-link-url"
            label={t.url}
            name="url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
            placeholder="https://..."
            required
          />
          <SelectField
            id="new-link-icon"
            label={t.icon}
            name="iconKey"
            value={iconKey}
            onChange={(e) => setIconKey(e.target.value as LinkIconKey)}
            required
          >
            {iconOptions()}
          </SelectField>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending}
          >
            {t.add}
          </button>
        </form>
      </Panel>

      <AppGrid>
        {links && links.length > 0 ? (
          links.map((link) => <LinkCard key={link.id} link={link} />)
        ) : (
          <EmptyState>{t.noLinks}</EmptyState>
        )}
      </AppGrid>
    </>
  );
}
